/**
 * RespondRfiModal — complete (or draft) the response to an RFI.
 *
 * The RFI is created (via a thread) with only the question side. The response is added later here:
 *   - Save draft  → saves the response text, keeps the RFI open (come back and edit anytime).
 *   - Submit response → saves the response + returned date, marks the RFI closed, and logs a
 *     'Thread Closed' activity on the linked Cortex thread.
 *
 * Opens via CustomEvent('open-rfi-respond', { detail: { rfi } }) — rfi is the full Supabase row.
 */

import { useState, useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { updateRfi } from '../services/rfis.js'
import { closeThread } from '../services/threads.js'
import MarkdownField from './MarkdownField.jsx'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

export default function RespondRfiModal() {
  const [open, setOpen]         = useState(false)
  const [rfi, setRfi]           = useState(null)
  const [response, setResponse] = useState('')
  const [returned, setReturned] = useState('')
  const [busy, setBusy]         = useState('')   // '' | 'draft' | 'submit'
  const [error, setError]       = useState(null)
  // Always points at the latest handleClose so the Escape/X handlers never act on stale state.
  const closeRef = useRef(() => {})

  useEffect(() => {
    function onOpen(e) {
      const r = (e.detail && e.detail.rfi) || null
      setRfi(r)
      setResponse(r?.response || '')
      setReturned(r?.date_returned || '')
      setError(null); setBusy(''); setOpen(true)
    }
    window.addEventListener('open-rfi-respond', onOpen)
    return () => window.removeEventListener('open-rfi-respond', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    // Escape routes through handleClose (same as the X) so it never discards unsaved work.
    function onKey(ev) { if (ev.key === 'Escape') closeRef.current() }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey) }
  }, [open])

  function refresh() {
    if (rfi?.contract_id && ctx.rfiCache) ctx.rfiCache[rfi.contract_id] = undefined
    if (rfi?.thread_airtable_id && ctx.caThreadCache) ctx.caThreadCache[rfi.thread_airtable_id] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  // Persist a draft: the response text (+ any return date) with status 'Draft Response'. Mirrors the
  // milestone draft/post split — a draft keeps the RFI open; only Submit closes it.
  async function saveDraft() {
    if (!rfi) return
    setBusy('draft'); setError(null)
    try {
      const fields = { response: response || null, date_returned: returned || null }
      if (response.trim()) fields.status = 'Draft Response'   // never flag an empty field as a draft
      await updateRfi(rfi.id, fields)
      refresh(); setOpen(false)
    } catch (ex) { setError(ex.message || 'Failed to save draft'); setBusy('') }
  }

  // Close path (X / Escape). Backdrop clicks do NOT call this. Auto-saves unsaved response text as a
  // draft so closing never loses work; an empty field closes without creating a draft or touching status.
  async function handleClose() {
    if (busy) return
    const text = (response || '').trim()
    const changed = response !== (rfi?.response || '') || (returned || '') !== (rfi?.date_returned || '')
    if (rfi && text && changed) {
      setBusy('draft'); setError(null)
      try {
        await updateRfi(rfi.id, { response, date_returned: returned || null, status: 'Draft Response' })
        refresh()
      } catch (ex) { setError(ex.message || 'Failed to save draft'); setBusy(''); return }  // keep open on failure
    }
    setBusy(''); setOpen(false)
  }
  closeRef.current = handleClose

  async function submitResponse() {
    if (!rfi) return
    if (!response.trim()) { setError('Enter a response before submitting.'); return }
    if (!returned)        { setError('Select the date returned to the GC before submitting.'); return }
    setBusy('submit'); setError(null)
    try {
      await updateRfi(rfi.id, { response, date_returned: returned, status: 'closed' })
      // Close the linked Cortex thread (logs a 'Thread Closed' activity + flips the Closed flag).
      if (rfi.thread_airtable_id) {
        try { await closeThread(rfi.thread_airtable_id, ctx.currentMemberId || null, 'RFI responded.') } catch (_) { /* non-fatal */ }
      }
      refresh(); setOpen(false)
    } catch (ex) { setError(ex.message || 'Failed to submit response'); setBusy('') }
  }

  if (!open) return null

  const num = rfi?.rfi_number != null ? ('RFI #' + rfi.rfi_number) : 'RFI'
  const subj = rfi?.subject ? ' — ' + rfi.subject : ''

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="RespondRfiModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 540, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Respond to RFI</span>
          <button onClick={handleClose} title="Close (keeps a draft of any response text)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '8px 10px', marginBottom: 16, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>
          {num}{subj}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Response</label>
            <MarkdownField value={response} onChange={setResponse} />
          </div>
          <div style={{ width: 180 }}>
            <label style={labelStyle}>Returned to GC</label>
            <input type="date" style={inputStyle} value={returned} onChange={e => setReturned(e.target.value)} />
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 3 }}>Required to submit.</div>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={saveDraft} disabled={!!busy} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 500, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: busy ? 'default' : 'pointer', color: 'var(--aj-text)' }}>
              {busy === 'draft' ? 'Saving…' : 'Save draft'}
            </button>
            <button onClick={submitResponse} disabled={!!busy} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: busy ? 'var(--aj-bg-muted)' : '#07272D', color: busy ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: busy ? 'default' : 'pointer' }}>
              {busy === 'submit' ? 'Submitting…' : 'Submit response'}
            </button>
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', textAlign: 'right' }}>
            Submitting requires a return date and closes the RFI and its thread. Closing with the × keeps a draft.
          </div>
        </div>
      </div>
    </div>
  )
}
